import "pixi.js/unsafe-eval";

import { Application, Container, Graphics, Text, type Ticker } from "pixi.js";

export type RenderQuality = "auto" | "high" | "balanced" | "low";
export type WeatherKind = "lightning" | "rain" | "wind" | "hail";

export interface RenderEnemy {
  readonly id: string;
  readonly kind: string;
  readonly x: number;
  readonly y: number;
  readonly health: number;
  readonly maxHealth: number;
  readonly radius?: number;
  readonly boss?: boolean;
  readonly statuses?: readonly string[];
  readonly affinity?: string;
}

export interface RenderEvent {
  readonly id?: string;
  readonly type: string;
  readonly sourceX?: number;
  readonly sourceY?: number;
  readonly targetX?: number;
  readonly targetY?: number;
  readonly value?: number;
  readonly critical?: boolean;
  readonly weather?: WeatherKind;
}

export interface RenderZone {
  readonly id: string;
  readonly kind: "rain" | "cyclone" | "squall" | "static";
  readonly x: number;
  readonly y: number;
  readonly radius: number;
  readonly expiresAtTick: number;
}

export interface RenderSnapshot {
  readonly tick: number;
  readonly width: number;
  readonly height: number;
  readonly integrity: number;
  readonly maxIntegrity: number;
  readonly shield?: number;
  readonly enemies: readonly RenderEnemy[];
  readonly zones?: readonly RenderZone[];
  readonly events?: readonly RenderEvent[];
  readonly weather?: readonly WeatherKind[];
  readonly bossActive?: boolean;
}

export interface RendererOptions {
  readonly quality?: RenderQuality;
  readonly reducedMotion?: boolean;
  readonly highContrast?: boolean;
  readonly damageNumbers?: "all" | "critical" | "off";
}

export interface RendererPort {
  mount(element: HTMLElement): Promise<void>;
  present(snapshot: RenderSnapshot): void;
  configure(options: RendererOptions): void;
  pause(): void;
  resume(): void;
  destroy(): void;
}

interface EnemyView {
  readonly root: Container;
  readonly body: Graphics;
  readonly health: Graphics;
  readonly status: Graphics;
  targetX: number;
  targetY: number;
  targetHealth: number;
  maxHealth: number;
  kind: string;
  radius: number;
  boss: boolean;
  missingFrames: number;
}

interface EffectView {
  readonly root: Container;
  readonly graphic: Graphics;
  readonly label: Text | undefined;
  age: number;
  duration: number;
  kind: string;
}

const PALETTE = {
  void: 0x030914,
  deep: 0x071525,
  grid: 0x18415a,
  cyan: 0x66e7ff,
  electric: 0xb6f4ff,
  rain: 0x4aa9ff,
  wind: 0x70f5cf,
  hail: 0xb99aff,
  warning: 0xffbd5c,
  breach: 0xff6b72,
  healthy: 0x6ef1bb,
  glass: 0x143c55,
  frost: 0xa4d8ff,
  fire: 0xff8b5c,
  dust: 0xe6ba7b,
};

const CORE_X = 500;
const CORE_Y = 700;
const MAX_EFFECTS: Readonly<Record<Exclude<RenderQuality, "auto">, number>> = {
  high: 90,
  balanced: 52,
  low: 24,
};

const weatherColor = (weather: WeatherKind | undefined): number => {
  switch (weather) {
    case "rain":
      return PALETTE.rain;
    case "wind":
      return PALETTE.wind;
    case "hail":
      return PALETTE.hail;
    default:
      return PALETTE.cyan;
  }
};

const enemyColor = (
  enemy: Pick<RenderEnemy, "kind" | "affinity" | "boss">,
): number => {
  if (enemy.boss === true) return PALETTE.warning;
  if (enemy.affinity === "fire") return PALETTE.fire;
  if (enemy.affinity === "dust") return PALETTE.dust;
  if (enemy.affinity === "frost") return PALETTE.frost;
  if (enemy.kind.includes("runner")) return 0xff91c7;
  if (enemy.kind.includes("brute") || enemy.kind.includes("anchor"))
    return 0xbb94ff;
  if (enemy.kind.includes("shell")) return 0xffd169;
  return 0x7ddff5;
};

const resolveQuality = (
  quality: RenderQuality,
): Exclude<RenderQuality, "auto"> => {
  if (quality !== "auto") return quality;
  const reduced = globalThis.matchMedia(
    "(prefers-reduced-motion: reduce)",
  ).matches;
  const memory =
    (navigator as Navigator & { deviceMemory?: number }).deviceMemory ?? 4;
  const cores = navigator.hardwareConcurrency || 4;
  if (reduced || memory <= 2 || cores <= 4) return "low";
  if (memory >= 8 && cores >= 8) return "high";
  return "balanced";
};

export class PixiRendererAdapter implements RendererPort {
  private readonly app = new Application();
  private readonly world = new Container();
  private readonly groundLayer = new Container();
  private readonly zoneLayer = new Container();
  private readonly enemyLayer = new Container();
  private readonly chamberLayer = new Container();
  private readonly effectsLayer = new Container();
  private readonly foregroundLayer = new Container();
  private readonly enemyViews = new Map<string, EnemyView>();
  private readonly zoneViews = new Map<string, Graphics>();
  private readonly effects: EffectView[] = [];
  private readonly integritySegments: Graphics[] = [];
  private chamberPulse = new Graphics();
  private chamberSystems = new Graphics();
  private atmosphere = new Graphics();
  private snapshot: RenderSnapshot | undefined;
  private resizeElement: HTMLElement | undefined;
  private mounted = false;
  private options: Required<RendererOptions> = {
    quality: "auto",
    reducedMotion: false,
    highContrast: false,
    damageNumbers: "all",
  };

  async mount(element: HTMLElement): Promise<void> {
    if (this.mounted) return;
    this.resizeElement = element;
    await this.app.init({
      background: PALETTE.void,
      antialias: true,
      autoDensity: true,
      resolution: Math.min(globalThis.devicePixelRatio || 1, 2),
      resizeTo: element,
      preference: "webgl",
      powerPreference: "high-performance",
    });
    this.app.canvas.setAttribute("aria-hidden", "true");
    this.app.canvas.className = "stormglass-canvas";
    element.append(this.app.canvas);
    this.world.addChild(
      this.groundLayer,
      this.zoneLayer,
      this.enemyLayer,
      this.chamberLayer,
      this.effectsLayer,
      this.foregroundLayer,
    );
    this.app.stage.addChild(this.world);
    this.drawStaticScene();
    this.app.ticker.add(this.animate);
    this.app.renderer.on("resize", this.layout);
    this.layout();
    this.mounted = true;
  }

  present(snapshot: RenderSnapshot): void {
    this.snapshot = snapshot;
    this.syncZones(snapshot.zones ?? [], snapshot.tick);
    this.syncEnemies(snapshot.enemies);
    this.syncIntegrity(snapshot);
    this.syncChamberSystems(snapshot.weather ?? ["lightning"]);
    for (const event of snapshot.events ?? []) this.spawnEvent(event);
  }

  configure(options: RendererOptions): void {
    this.options = {
      quality: options.quality ?? this.options.quality,
      reducedMotion: options.reducedMotion ?? this.options.reducedMotion,
      highContrast: options.highContrast ?? this.options.highContrast,
      damageNumbers: options.damageNumbers ?? this.options.damageNumbers,
    };
    this.atmosphere.alpha = this.options.highContrast ? 0.28 : 0.55;
  }

  pause(): void {
    this.app.ticker.stop();
  }

  resume(): void {
    this.app.ticker.start();
  }

  destroy(): void {
    if (!this.mounted) return;
    this.app.renderer.off("resize", this.layout);
    this.app.ticker.remove(this.animate);
    this.app.destroy({ removeView: true }, { children: true });
    this.enemyViews.clear();
    this.zoneViews.clear();
    this.effects.length = 0;
    this.resizeElement = undefined;
    this.mounted = false;
  }

  private readonly layout = (): void => {
    const viewportWidth =
      this.resizeElement?.clientWidth ||
      this.app.renderer.width / this.app.renderer.resolution;
    const viewportHeight =
      this.resizeElement?.clientHeight ||
      this.app.renderer.height / this.app.renderer.resolution;
    const scale = Math.min(viewportWidth / 1000, viewportHeight / 1400);
    this.world.scale.set(scale);
    this.world.position.set(
      (viewportWidth - 1000 * scale) / 2,
      (viewportHeight - 1400 * scale) / 2,
    );
  };

  private drawStaticScene(): void {
    const backdrop = new Graphics();
    backdrop.rect(0, 0, 1000, 1400).fill(PALETTE.deep);
    backdrop.circle(CORE_X, CORE_Y, 610).fill({ color: 0x09283a, alpha: 0.7 });
    backdrop
      .circle(CORE_X, CORE_Y, 500)
      .stroke({ color: PALETTE.grid, width: 2, alpha: 0.34 });
    backdrop
      .circle(CORE_X, CORE_Y, 370)
      .stroke({ color: PALETTE.grid, width: 2, alpha: 0.28 });
    backdrop
      .circle(CORE_X, CORE_Y, 235)
      .stroke({ color: PALETTE.grid, width: 2, alpha: 0.22 });
    for (let index = 0; index < 24; index += 1) {
      const angle = (index / 24) * Math.PI * 2;
      const outerX = CORE_X + Math.cos(angle) * 570;
      const outerY = CORE_Y + Math.sin(angle) * 570;
      const innerX = CORE_X + Math.cos(angle) * 120;
      const innerY = CORE_Y + Math.sin(angle) * 120;
      backdrop
        .moveTo(innerX, innerY)
        .lineTo(outerX, outerY)
        .stroke({ color: PALETTE.grid, width: 1, alpha: 0.13 });
    }
    this.groundLayer.addChild(backdrop);

    this.atmosphere = new Graphics();
    for (let index = 0; index < 18; index += 1) {
      const angle = index * 2.37;
      const radius = 250 + (index % 5) * 68;
      this.atmosphere
        .circle(
          CORE_X + Math.cos(angle) * radius,
          CORE_Y + Math.sin(angle) * radius,
          42 + (index % 3) * 18,
        )
        .fill({
          color: index % 4 === 0 ? PALETTE.rain : PALETTE.glass,
          alpha: 0.055,
        });
    }
    this.groundLayer.addChild(this.atmosphere);

    const chamber = new Graphics();
    chamber
      .circle(CORE_X, CORE_Y + 9, 114)
      .fill({ color: 0x020812, alpha: 0.65 });
    chamber
      .circle(CORE_X, CORE_Y, 104)
      .fill({ color: PALETTE.glass, alpha: 0.7 })
      .stroke({ color: PALETTE.cyan, width: 3, alpha: 0.7 });
    chamber
      .circle(CORE_X, CORE_Y, 71)
      .fill({ color: 0x071b2c, alpha: 0.95 })
      .stroke({ color: PALETTE.cyan, width: 4, alpha: 0.8 });
    chamber
      .circle(CORE_X, CORE_Y, 35)
      .fill({ color: PALETTE.electric, alpha: 0.95 });
    chamber
      .moveTo(CORE_X - 22, CORE_Y)
      .lineTo(CORE_X, CORE_Y - 48)
      .lineTo(CORE_X + 17, CORE_Y - 5)
      .lineTo(CORE_X + 48, CORE_Y - 18)
      .stroke({ color: PALETTE.cyan, width: 6, alpha: 0.88 });
    this.chamberLayer.addChild(chamber);

    this.chamberPulse = new Graphics();
    this.chamberPulse
      .circle(CORE_X, CORE_Y, 55)
      .stroke({ color: PALETTE.electric, width: 4, alpha: 0.6 });
    this.chamberLayer.addChild(this.chamberPulse);

    this.chamberSystems = new Graphics();
    this.chamberLayer.addChild(this.chamberSystems);

    for (let index = 0; index < 20; index += 1) {
      const angle = (index / 20) * Math.PI * 2 - Math.PI / 2;
      const next = angle + (Math.PI * 2) / 20 - 0.045;
      const segment = new Graphics();
      segment
        .arc(CORE_X, CORE_Y, 132, angle, next)
        .stroke({ color: PALETTE.healthy, width: 13, alpha: 0.9 });
      this.integritySegments.push(segment);
      this.chamberLayer.addChild(segment);
    }
  }

  private syncIntegrity(snapshot: RenderSnapshot): void {
    const ratio =
      snapshot.maxIntegrity <= 0
        ? 0
        : snapshot.integrity / snapshot.maxIntegrity;
    const active = Math.ceil(ratio * this.integritySegments.length);
    this.integritySegments.forEach((segment, index) => {
      segment.alpha = index < active ? 1 : 0.12;
      segment.tint =
        ratio < 0.3 ? PALETTE.breach : ratio < 0.6 ? PALETTE.warning : 0xffffff;
    });
  }

  private syncZones(zones: readonly RenderZone[], tick: number): void {
    const activeIds = new Set<string>();
    for (const zone of zones) {
      activeIds.add(zone.id);
      let view = this.zoneViews.get(zone.id);
      if (view === undefined) {
        view = new Graphics();
        this.zoneViews.set(zone.id, view);
        this.zoneLayer.addChild(view);
      }
      view.clear();
      const color =
        zone.kind === "rain" || zone.kind === "squall"
          ? PALETTE.rain
          : zone.kind === "cyclone"
            ? PALETTE.wind
            : PALETTE.cyan;
      view
        .circle(zone.x, zone.y, zone.radius)
        .fill({ color, alpha: zone.kind === "rain" ? 0.09 : 0.055 });
      view
        .circle(zone.x, zone.y, zone.radius)
        .stroke({ color, width: 3, alpha: 0.5 });
      view
        .circle(zone.x, zone.y, zone.radius * 0.66)
        .stroke({ color, width: 2, alpha: 0.24 });
      if (zone.kind === "rain" || zone.kind === "squall") {
        for (let index = -2; index <= 2; index += 1) {
          view
            .moveTo(
              zone.x + index * zone.radius * 0.24,
              zone.y - zone.radius * 0.43,
            )
            .lineTo(
              zone.x + index * zone.radius * 0.24 - 14,
              zone.y + zone.radius * 0.42,
            )
            .stroke({ color, width: 2, alpha: 0.32 });
        }
      } else {
        view
          .arc(
            zone.x,
            zone.y,
            zone.radius * 0.82,
            tick * 0.04,
            tick * 0.04 + Math.PI * 1.35,
          )
          .stroke({ color, width: 7, alpha: 0.32 });
      }
    }
    for (const [id, view] of this.zoneViews) {
      if (!activeIds.has(id)) {
        view.destroy();
        this.zoneViews.delete(id);
      }
    }
  }

  private syncChamberSystems(weather: readonly WeatherKind[]): void {
    this.chamberSystems.clear();
    const systems = weather.filter((kind) => kind !== "lightning");
    systems.forEach((kind, index) => {
      const angle =
        -Math.PI / 2 + ((index + 1) / (systems.length + 1)) * Math.PI * 2;
      const x = CORE_X + Math.cos(angle) * 91;
      const y = CORE_Y + Math.sin(angle) * 91;
      const color = weatherColor(kind);
      this.chamberSystems
        .circle(x, y, 16)
        .fill({ color: PALETTE.deep, alpha: 0.92 })
        .stroke({ color, width: 4, alpha: 0.9 });
      this.chamberSystems.circle(x, y, 5).fill(color);
    });
  }

  private syncEnemies(enemies: readonly RenderEnemy[]): void {
    for (const view of this.enemyViews.values()) view.missingFrames += 1;
    for (const enemy of enemies) {
      let view = this.enemyViews.get(enemy.id);
      if (view === undefined) {
        view = this.createEnemyView(enemy);
        this.enemyViews.set(enemy.id, view);
        this.enemyLayer.addChild(view.root);
      }
      view.targetX = enemy.x;
      view.targetY = enemy.y;
      view.targetHealth = enemy.health;
      view.maxHealth = enemy.maxHealth;
      view.missingFrames = 0;
      this.redrawEnemy(view, enemy);
    }
    for (const [id, view] of this.enemyViews) {
      if (view.missingFrames > 1) {
        view.root.destroy({ children: true });
        this.enemyViews.delete(id);
      }
    }
  }

  private createEnemyView(enemy: RenderEnemy): EnemyView {
    const root = new Container();
    root.position.set(enemy.x, enemy.y);
    const body = new Graphics();
    const health = new Graphics();
    const status = new Graphics();
    root.addChild(body, health, status);
    return {
      root,
      body,
      health,
      status,
      targetX: enemy.x,
      targetY: enemy.y,
      targetHealth: enemy.health,
      maxHealth: enemy.maxHealth,
      kind: enemy.kind,
      radius: enemy.radius ?? (enemy.boss === true ? 62 : 26),
      boss: enemy.boss ?? false,
      missingFrames: 0,
    };
  }

  private redrawEnemy(view: EnemyView, enemy: RenderEnemy): void {
    const radius = enemy.radius ?? view.radius;
    const color = enemyColor(enemy);
    view.kind = enemy.kind;
    view.radius = radius;
    view.boss = enemy.boss ?? false;
    view.body.clear();
    view.body.circle(0, 8, radius).fill({ color: 0x01060d, alpha: 0.5 });
    if (enemy.kind.includes("runner")) {
      view.body
        .poly([
          0,
          -radius,
          radius * 0.8,
          radius,
          0,
          radius * 0.55,
          -radius * 0.8,
          radius,
        ])
        .fill({ color, alpha: 0.94 });
    } else if (enemy.kind.includes("brute") || enemy.kind.includes("anchor")) {
      view.body
        .roundRect(-radius, -radius, radius * 2, radius * 2, 8)
        .fill({ color, alpha: 0.88 })
        .stroke({ color: 0xffffff, width: 3, alpha: 0.4 });
    } else {
      view.body
        .circle(0, 0, radius)
        .fill({ color, alpha: 0.88 })
        .stroke({
          color: 0xd9f8ff,
          width: enemy.boss === true ? 5 : 2,
          alpha: 0.55,
        });
    }
    view.body
      .circle(-radius * 0.28, -radius * 0.18, Math.max(3, radius * 0.12))
      .fill(0x04111d);
    view.body
      .circle(radius * 0.28, -radius * 0.18, Math.max(3, radius * 0.12))
      .fill(0x04111d);

    view.health.clear();
    const width = radius * 2;
    view.health
      .roundRect(-radius, radius + 9, width, 7, 3)
      .fill({ color: 0x02060b, alpha: 0.85 });
    view.health
      .roundRect(
        -radius,
        radius + 9,
        width * Math.max(0, enemy.health / Math.max(1, enemy.maxHealth)),
        7,
        3,
      )
      .fill(enemy.boss === true ? PALETTE.warning : PALETTE.healthy);

    view.status.clear();
    (enemy.statuses ?? []).slice(0, 3).forEach((status, index) => {
      const statusColor =
        status === "wet"
          ? PALETTE.rain
          : status === "chill" || status === "freeze"
            ? PALETTE.frost
            : PALETTE.warning;
      view.status.circle(-10 + index * 10, -radius - 10, 4).fill(statusColor);
    });
  }

  private spawnEvent(event: RenderEvent): void {
    const quality = resolveQuality(this.options.quality);
    if (this.effects.length >= MAX_EFFECTS[quality]) return;
    const graphic = new Graphics();
    const root = new Container();
    let label: Text | undefined;
    const sourceX = event.sourceX ?? CORE_X;
    const sourceY = event.sourceY ?? CORE_Y;
    const targetX = event.targetX ?? CORE_X;
    const targetY = event.targetY ?? CORE_Y;
    const color = weatherColor(event.weather);
    if (
      event.type.includes("damage") ||
      event.type.includes("attack") ||
      event.type.includes("lightning")
    ) {
      graphic.moveTo(sourceX, sourceY);
      const midpointX = (sourceX + targetX) / 2 + (Math.random() - 0.5) * 34;
      const midpointY = (sourceY + targetY) / 2 + (Math.random() - 0.5) * 34;
      graphic
        .lineTo(midpointX, midpointY)
        .lineTo(targetX, targetY)
        .stroke({ color, width: event.critical === true ? 8 : 4, alpha: 0.95 });
      graphic
        .circle(targetX, targetY, event.critical === true ? 32 : 18)
        .stroke({ color, width: 3, alpha: 0.72 });
      if (
        event.value !== undefined &&
        this.options.damageNumbers !== "off" &&
        (this.options.damageNumbers === "all" || event.critical === true)
      ) {
        label = new Text({
          text: `${event.critical === true ? "CRIT " : ""}${String(Math.round(event.value))}`,
          style: {
            fill: event.critical === true ? "#ffdf82" : "#d5f8ff",
            fontFamily: "system-ui",
            fontSize: event.critical === true ? 28 : 22,
            fontWeight: "800",
            stroke: { color: "#02101c", width: 5 },
          },
        });
        label.anchor.set(0.5);
        label.position.set(targetX, targetY - 36);
        root.addChild(label);
      }
    } else if (event.type.includes("breach") || event.type.includes("impact")) {
      graphic
        .circle(CORE_X, CORE_Y, 150)
        .stroke({ color: PALETTE.breach, width: 18, alpha: 0.7 });
    } else if (event.type.includes("reaction")) {
      graphic.circle(targetX, targetY, 14).fill({ color, alpha: 0.25 });
      graphic
        .circle(targetX, targetY, 78)
        .stroke({ color, width: 8, alpha: 0.75 });
    } else {
      return;
    }
    root.addChildAt(graphic, 0);
    this.effectsLayer.addChild(root);
    this.effects.push({
      root,
      graphic,
      label,
      age: 0,
      duration: this.options.reducedMotion ? 0.12 : 0.42,
      kind: event.type,
    });
  }

  private readonly animate = (ticker: Ticker): void => {
    const delta = Math.min(2, ticker.deltaTime);
    for (const view of this.enemyViews.values()) {
      const easing = this.options.reducedMotion ? 1 : 1 - Math.pow(0.78, delta);
      view.root.x += (view.targetX - view.root.x) * easing;
      view.root.y += (view.targetY - view.root.y) * easing;
    }
    const pulse = this.options.reducedMotion
      ? 1
      : 1 + Math.sin(ticker.lastTime * 0.004) * 0.08;
    this.chamberPulse.scale.set(pulse);
    this.chamberPulse.alpha = this.snapshot?.bossActive === true ? 0.95 : 0.62;
    if (!this.options.reducedMotion)
      this.atmosphere.rotation += 0.00012 * delta;
    for (let index = this.effects.length - 1; index >= 0; index -= 1) {
      const effect = this.effects[index];
      if (effect === undefined) continue;
      effect.age += ticker.deltaMS / 1000;
      const progress = effect.age / effect.duration;
      effect.root.alpha = Math.max(0, 1 - progress);
      if (effect.label !== undefined) effect.label.y -= 0.7 * delta;
      if (effect.kind.includes("reaction"))
        effect.root.scale.set(1 + progress * 0.4);
      if (progress >= 1) {
        effect.root.destroy({ children: true });
        this.effects.splice(index, 1);
      }
    }
  };
}

export const createRenderer = (): RendererPort => new PixiRendererAdapter();
